import React from 'react';
import classNames from 'classnames';

export type DividerColor = 'regular' | 'subtle' | 'special';
export type DividerOrientation = 'horizontal' | 'vertical';

export type DividerProps = {
  /** "regular", "subtle" or "special", the type options for the divider */
  type?: DividerColor;
  /** an optional prop. by providing a text prop the divider becomes a split divider with text value. Only supported for horizontal orientation. */
  text?: string;
  /** "horizontal" or "vertical" orientation of the divider. Defaults to "horizontal". */
  orientation?: DividerOrientation;
};

const horizontalColorClasses: Record<DividerColor, string> = {
  regular:
    'before:border-foreground-regular after:border-foreground-regular text-subtle',
  subtle:
    'before:border-foreground-subtle after:border-foreground-subtle text-regular',
  special:
    'before:border-foreground-special after:border-foreground-special text-primary-brand',
};

const verticalColorClasses: Record<DividerColor, string> = {
  regular: 'border-foreground-regular',
  subtle: 'border-foreground-subtle',
  special: 'border-foreground-special',
};

/**
 * A futuro divider
 *
 * @argument type string - "regular", "subtle" or "special", the type options for
 *     the divider
 * @argument text string - an optional prop. by providing a text prop the divider becomes a split divider
 *    with text value. Only supported for horizontal orientation.
 * @argument orientation string - "horizontal" or "vertical". Defaults to "horizontal".
 *
 * Based off the designs in the figma file:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=2839%3A12379
 *
 * @example
 *
 * <Divider type="regular" />
 * <Divider type="subtle" text="Hello World" />
 * <Divider type="special" text="Hello World" />
 * <Divider orientation="vertical" />
 */

const Divider = (props: DividerProps) => {
  const { type = 'regular', text, orientation = 'horizontal' } = props;

  if (orientation === 'vertical') {
    return (
      <div
        className={classNames(
          'self-stretch border-0 border-l border-solid',
          verticalColorClasses[type]
        )}
      />
    );
  }

  return (
    <div
      className={classNames(
        "flex w-full items-center text-center text-body-small before:flex-[1] before:border-0 before:border-t before:border-solid before:content-[''] after:flex-1 after:border-0 after:border-t after:border-solid after:content-['']",
        horizontalColorClasses[type],
        { 'before:mr-s after:ml-s': Boolean(text) }
      )}
    >
      {text || null}
    </div>
  );
};

export default Divider;
